import React, { useState, useEffect } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { 
  LuLayoutDashboard, 
  LuUsers, 
  LuHand, 
  LuTrophy, 
  LuTicket, 
  LuArrowLeft, 
  LuMenu, 
  LuX,
  LuChevronLeft,
  LuChevronRight,
  LuBox,
  LuPackage,
  LuMail as LuEnvelope
} from "react-icons/lu";
import { useAuth } from "../context/AuthContext";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../lib/firebase";

export const AdminLayout = () => {
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const { user } = useAuth();
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    const checkRole = async () => {
      if (!user) return;

      try {
        // Check Firestore only
        const userDocRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          setUserRole(userDoc.data().role);
        }
      } catch (error) {
        console.error("Error checking role:", error);
      }
    };

    checkRole();
  }, [user]);

  const allMenuItems = [
    { path: "/admin", label: "Dashboard", icon: <LuLayoutDashboard size={22} />, roles: ["admin"] },
    { path: "/admin/participants", label: "Participants", icon: <LuUsers size={22} />, roles: ["admin"] },
    { label: "Race Results", path: "/admin/race-results", icon: <LuTrophy size={20} />, roles: ["admin"] },
    { label: "Vouchers", path: "/admin/vouchers", icon: <LuTicket size={20} />, roles: ["admin"] },
    { label: "Email Center", path: "/admin/broadcast-email", icon: <LuEnvelope size={20} />, roles: ["admin"] },
    { label: "RPC System", path: "/admin/rpc", icon: <LuBox size={20} />, roles: ["admin"] },
    { label: "RPC SMM", path: "/smm/rpc", icon: <LuPackage size={20} />, roles: ["admin", "rpc"] },
  ];

  const menuItems = userRole === "rpc"
    ? allMenuItems.filter(item => item.roles.includes("rpc"))
    : allMenuItems;

  // The RPC desk has exactly one screen, so the sidebar is dropped entirely
  // and the page renders on its own.
  if (userRole === "rpc") {
    return (
      <div className="min-h-screen bg-slate-50">
        <main className="p-4">
          <div className="max-w-[1600px] mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside
        className={`${
          isSidebarOpen ? "w-48" : "w-16"
        } bg-white border-r border-slate-200 transition-all duration-300 flex flex-col fixed left-0 top-0 h-screen z-40 overflow-hidden`}
      >
        {/* Sidebar Header */}
        <div className={`flex items-center border-b border-slate-200 p-4 ${isSidebarOpen ? "justify-between" : "justify-center"}`}>
          {isSidebarOpen && (
            <h1 className="font-semibold text-slate-800 text-xl leading-none whitespace-nowrap">
              {userRole === "rpc" ? "RPC Panel" : "Admin Panel"}
            </h1>
          )}
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="text-slate-400 hover:text-slate-700 transition-colors"
            aria-label="Toggle sidebar"
          >
            {isSidebarOpen ? <LuChevronLeft size={16} /> : <LuChevronRight size={16} />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 custom-scrollbar">
          <ul className="space-y-1">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path || (item.path !== "/admin" && location.pathname.startsWith(item.path));
              return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`relative flex items-center px-4 py-3 rounded-lg transition-all duration-200 group overflow-hidden ${
                    isActive
                      ? "bg-slate-100 text-slate-900 font-semibold"
                      : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                  }`}
                >
                  <span className={`flex-shrink-0 transition-colors ${isActive ? "text-slate-900" : "text-slate-400 group-hover:text-slate-700"}`}>
                    {item.icon}
                  </span>
                  
                  <span 
                    className={`ml-4 text-sm whitespace-nowrap transition-all duration-300 origin-left ${
                      !isSidebarOpen ? "opacity-0 translate-x-4 w-0" : "opacity-100 translate-x-0 w-auto"
                    }`}
                  >
                    {item.label}
                  </span>
                </Link>
              </li>
            )})}
          </ul>
        </nav>

        {/* User / Footer */}
        <div className="p-4 border-t border-slate-200 bg-white">
          <Link
            to="/"
            className={`flex items-center w-full px-4 py-3 text-slate-500 hover:bg-slate-100 hover:text-slate-900 rounded-lg transition-all group ${!isSidebarOpen && "justify-center px-0"}`}
          >
            <LuArrowLeft size={20} />
            <span 
               className={`ml-4 font-medium text-sm transition-all duration-300 ${!isSidebarOpen ? "hidden" : "block"}`}
            >
              Homepage
            </span>
          </Link>
        </div>
      </aside>

      {/* Main Content Wrapper */}
      <div 
        className={`flex-1 transition-all duration-300 ${
           isSidebarOpen ? "ml-48" : "ml-16"
        }`}
      >
        {/* Top Navbar Placeholder (Optional, usually for breadcrumbs or user profile) */}
        {/* <header className="h-[70px] bg-white border-b border-gray-200 sticky top-0 z-30 px-8 flex items-center justify-between">
           <h2 className="text-gray-700 font-semibold capitailize">
             {location.pathname.split('/').pop().replace('-', ' ') || 'Dashboard'}
           </h2>
        </header> */}

        {/* Content Area */}
        <main className="p-4 min-h-screen">
          <div className="max-w-[1600px] mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};
